import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, MoreHorizontal, RefreshCw, AlertCircle, Plus, Edit, Save, X, Edit3 } from 'lucide-react';
import { stcMovementPriorityService, StcMovementPriorityDto, CreateStcMovementPriorityRequest } from '../../../../../services/stcMovementPriorityService';
import { stcMovementTypeService, StcMovementTypeDto } from '../../../../../services/stcMovementTypeService';
import { stcBinTypeService, StcBinTypeDto } from '../../../../../services/stcBinTypeService';
import { useAuth } from '../../../../../contexts/AuthContext';

interface ColumnFilter {
    [key: string]: string;
}

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

interface SelectedRows {
    [key: string]: boolean;
}

interface MovementPriorityDataTableProps {
    selectedRows: SelectedRows;
    onRowSelection: (rowId: string, selected: boolean) => void;
    onBulkSelection: (rows: SelectedRows) => void;
    onDataChange: (data: StcMovementPriorityDto[]) => void;
    onOpenSidePanel: () => void;
    refreshTrigger?: number;
}

export const MovementPriorityDataTable: React.FC<MovementPriorityDataTableProps> = ({
    selectedRows,
    onRowSelection,
    onBulkSelection,
    onDataChange,
    onOpenSidePanel,
    refreshTrigger
}) => {
    const [columnFilters, setColumnFilters] = useState<ColumnFilter>({});
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [data, setData] = useState<StcMovementPriorityDto[]>([]);
    const [movementTypes, setMovementTypes] = useState<StcMovementTypeDto[]>([]);
    const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editingData, setEditingData] = useState<Partial<StcMovementPriorityDto>>({});
    
    const { user } = useAuth();

    // Oppdaterte felter - bruker beskrivelser i stedet for ID-er
    const fields = ['movementType', 'binType', 'fromPriority', 'toPriority'];
    
    useEffect(() => {
        loadData();
        loadMovementTypes();
        loadBinTypes();
    }, []);

    // React to refresh trigger from parent component
    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            loadData();
        }
    }, [refreshTrigger]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');
            
            let movementPriorities: StcMovementPriorityDto[] = [];
            
            try {
                movementPriorities = await stcMovementPriorityService.getCurrent();
            } catch (err) {
                try {
                    movementPriorities = await stcMovementPriorityService.getAll();
                } catch (err2) {
                    movementPriorities = [];
                }
            }
            
            setData(movementPriorities);
            onDataChange(movementPriorities);
        } catch (err: any) {
            setError(err.message || 'Feil ved lasting av data');
        } finally {
            setLoading(false);
        }
    };

    const CreateForm: React.FC<{ onClose: () => void; onSave: (request: CreateStcMovementPriorityRequest) => void }> = ({ onClose, onSave }) => {
        const [formData, setFormData] = useState<CreateStcMovementPriorityRequest>({
            companyNo: user?.companyNo || '001',
            movementTypeNo: movementTypes.length > 0 ? movementTypes[0].movementTypeNo : 1,
            binTypeNo: binTypes.length > 0 ? binTypes[0].binTypeNo : 1,
            fromPriority: 1,
            toPriority: 1
        });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            onSave(formData);
        };

        return (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Opprett ny flytteprioritet</h4>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Selskap</label>
                            <input
                                type="text"
                                value={formData.companyNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, companyNo: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Flyttetype</label>
                            <select
                                value={formData.movementTypeNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, movementTypeNo: parseInt(e.target.value) }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            >
                                {movementTypes.map(mt => (
                                    <option key={mt.movementTypeNo} value={mt.movementTypeNo}>
                                        {mt.description1}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Lokasjonstype</label>
                            <select
                                value={formData.binTypeNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, binTypeNo: parseInt(e.target.value) }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            >
                                {binTypes.map(bt => (
                                    <option key={bt.binTypeNo} value={bt.binTypeNo}>
                                        {bt.description1 || `BinType ${bt.binTypeNo}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Fra prioritet</label>
                            <input
                                type="number"
                                value={formData.fromPriority}
                                onChange={(e) => setFormData(prev => ({ ...prev, fromPriority: parseInt(e.target.value) }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Til prioritet</label>
                            <input
                                type="number"
                                value={formData.toPriority}
                                onChange={(e) => setFormData(prev => ({ ...prev, toPriority: parseInt(e.target.value) }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="submit"
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Opprett
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Avbryt
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    const handleCreate = async (request: CreateStcMovementPriorityRequest) => {
        try {
            setLoading(true);
            await stcMovementPriorityService.create(request);
            setShowCreateForm(false);
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse');
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (row: StcMovementPriorityDto) => {
        setEditingRowId(row.id);
        setEditingData({ ...row });
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setEditingData({});
    };

    const saveEdit = async () => {
        if (!editingRowId || !editingData.companyNo || !editingData.movementTypeNo || !editingData.binTypeNo) {
            return;
        }

        try {
            setLoading(true);
            await stcMovementPriorityService.update(
                editingData.companyNo,
                editingData.movementTypeNo,
                editingData.binTypeNo,
                {
                    fromPriority: editingData.fromPriority || 0,
                    toPriority: editingData.toPriority || 0
                }
            );
            
            setEditingRowId(null);
            setEditingData({});
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Feil ved lagring');
        } finally {
            setLoading(false);
        }
    };

    const updateEditingField = (field: keyof StcMovementPriorityDto, value: string | number) => {
        setEditingData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const loadMovementTypes = async () => {
        try {
            let types: StcMovementTypeDto[] = [];
            
            try {
                types = await stcMovementTypeService.getCurrent();
            } catch (err) {
                try {
                    types = await stcMovementTypeService.getAll();
                } catch (err2) {
                    types = [];
                }
            }
            
            setMovementTypes(types);
        } catch (err: any) {
            console.error('Error loading movement types:', err);
        }
    };

    const loadBinTypes = async () => {
        try {
            let types: StcBinTypeDto[] = [];
            
            try {
                types = await stcBinTypeService.getCurrent();
            } catch (err) {
                try {
                    types = await stcBinTypeService.getAll();
                } catch (err2) {
                    types = [];
                }
            }
            
            setBinTypes(types);
        } catch (err: any) {
            console.error('Error loading bin types:', err);
        }
    };

    const getMovementTypeDescription = (movementTypeNo: number): string => {
        const movementType = movementTypes.find(mt => mt.movementTypeNo === movementTypeNo);
        return movementType ? movementType.description1 : `Flyttetype ${movementTypeNo}`;
    };

    const getBinTypeDescription = (binTypeNo: number): string => {
        const binType = binTypes.find(bt => bt.binTypeNo === binTypeNo);
        return binType ? (binType.description1 || `BinType ${binTypeNo}`) : `BinType ${binTypeNo}`;
    };

    // Filter data based on column filters and global search
    const filteredData = useMemo(() => {
        let filtered = data;

        // Apply global filter
        if (globalFilter) {
            filtered = filtered.filter(row => {
                const movementTypeDesc = getMovementTypeDescription(row.movementTypeNo);
                const binTypeDesc = getBinTypeDescription(row.binTypeNo);
                return Object.values(row).some(value => 
                    value?.toString().toLowerCase().includes(globalFilter.toLowerCase())
                ) || movementTypeDesc.toLowerCase().includes(globalFilter.toLowerCase())
                  || binTypeDesc.toLowerCase().includes(globalFilter.toLowerCase());
            });
        }

        // Apply column filters
        Object.entries(columnFilters).forEach(([column, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(row => {
                    let fieldValue: any;
                    if (column === 'movementType') {
                        fieldValue = getMovementTypeDescription(row.movementTypeNo);
                    } else if (column === 'binType') {
                        fieldValue = getBinTypeDescription(row.binTypeNo);
                    } else {
                        fieldValue = (row as any)[column];
                    }
                    return fieldValue?.toString().toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });

        return filtered;
    }, [data, columnFilters, globalFilter, movementTypes, binTypes]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            let aValue: any, bValue: any;
            
            if (sortConfig.key === 'movementType') {
                aValue = getMovementTypeDescription(a.movementTypeNo);
                bValue = getMovementTypeDescription(b.movementTypeNo);
            } else if (sortConfig.key === 'binType') {
                aValue = getBinTypeDescription(a.binTypeNo);
                bValue = getBinTypeDescription(b.binTypeNo);
            } else {
                aValue = (a as any)[sortConfig.key];
                bValue = (b as any)[sortConfig.key];
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig, movementTypes, binTypes]);

    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleColumnFilter = (column: string, value: string) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        const newSelection: SelectedRows = {};
        if (checked) {
            sortedData.forEach(row => {
                newSelection[row.id.toString()] = true;
            });
        }
        onBulkSelection(newSelection);
    };

    const isAllSelected = sortedData.length > 0 && sortedData.every(row => selectedRows[row.id.toString()]);
    const isIndeterminate = sortedData.some(row => selectedRows[row.id.toString()]) && !isAllSelected;

    const getFieldLabel = (field: string): string => {
        const labels: { [key: string]: string } = {
            'movementType': 'Flyttetype',
            'binType': 'Lokasjonstype',
            'fromPriority': 'Fra prioritet',
            'toPriority': 'Til prioritet'
        };
        return labels[field] || field;
    };

    if (loading && data.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-3" />
                    <span className="text-gray-600">Laster flytteprioriteringer...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center text-red-600">
                    <AlertCircle className="w-6 h-6 mr-3" />
                    <div>
                        <p className="font-medium">Feil ved lasting av data</p>
                        <p className="text-sm text-gray-600">{error}</p>
                        <button
                            onClick={loadData}
                            className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Prøv igjen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Table Header Controls */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-gray-900">Flytteprioriteringer</h3>
                        <span className="text-sm text-gray-500">
                            ({sortedData.length} av {data.length} rader)
                        </span>
                        {loading && (
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                        )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {/* Global Search */}
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Søk i alle kolonner..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <button
                            onClick={() => setShowCreateForm(!showCreateForm)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Ny
                        </button>
                        
                        <button
                            onClick={onOpenSidePanel}
                            disabled={Object.values(selectedRows).filter(Boolean).length === 0}
                            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                                Object.values(selectedRows).filter(Boolean).length > 0
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Edit3 className="w-4 h-4" />
                            Rediger ({Object.values(selectedRows).filter(Boolean).length})
                        </button>
                        
                        <button 
                            onClick={loadData}
                            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span className="text-sm">Oppdater</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Create Form */}
            {showCreateForm && (
                <CreateForm 
                    onClose={() => setShowCreateForm(false)}
                    onSave={handleCreate}
                />
            )}

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            {/* Select All Checkbox */}
                            <th className="w-12 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isIndeterminate;
                                    }}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                            </th>
                            
                            {/* Column Headers */}
                            {fields.map(field => (
                                <th key={field} className="px-4 py-3 text-left">
                                    <div className="space-y-2">
                                        {/* Sort Header */}
                                        <button
                                            onClick={() => handleSort(field)}
                                            className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                        >
                                            <span>{getFieldLabel(field)}</span>
                                            {sortConfig.key === field ? (
                                                sortConfig.direction === 'asc' ? (
                                                    <ChevronUp className="w-3 h-3" />
                                                ) : (
                                                    <ChevronDown className="w-3 h-3" />
                                                )
                                            ) : (
                                                <div className="w-3 h-3" />
                                            )}
                                        </button>
                                        
                                        {/* Column Filter */}
                                        <input
                                            type="text"
                                            placeholder={`Filter ${getFieldLabel(field)}...`}
                                            value={columnFilters[field] || ''}
                                            onChange={(e) => handleColumnFilter(field, e.target.value)}
                                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </th>
                            ))}
                            
                            {/* Actions Column */}
                            <th className="w-16 px-4 py-3">
                                <span className="text-xs font-medium text-gray-700">Handlinger</span>
                            </th>
                        </tr>
                    </thead>
                    
                    <tbody className="divide-y divide-gray-200">
                        {sortedData.map(row => {
                            const isEditing = editingRowId === row.id;
                            
                            return (
                                <tr 
                                    key={row.id} 
                                    className={`hover:bg-gray-50 transition-colors ${
                                        selectedRows[row.id.toString()] ? 'bg-blue-50' : ''
                                    } ${
                                        isEditing ? 'bg-yellow-50' : ''
                                    }`}
                                >
                                    {/* Row Checkbox */}
                                    <td className="px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows[row.id.toString()] || false}
                                            onChange={(e) => onRowSelection(row.id.toString(), e.target.checked)}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            disabled={isEditing}
                                        />
                                    </td>
                                    
                                    {/* Data Columns */}
                                    {fields.map(field => (
                                        <td key={field} className="px-4 py-3">
                                            {isEditing && (field === 'fromPriority' || field === 'toPriority') ? (
                                                <input
                                                    type="number"
                                                    value={editingData[field as keyof StcMovementPriorityDto] || ''}
                                                    onChange={(e) => updateEditingField(field as keyof StcMovementPriorityDto, parseInt(e.target.value) || 0)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                    min="0"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-900">
                                                    {field === 'movementType' 
                                                        ? getMovementTypeDescription(row.movementTypeNo)
                                                        : field === 'binType'
                                                        ? getBinTypeDescription(row.binTypeNo)
                                                        : (row as any)[field]
                                                    }
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                    
                                    {/* Actions */}
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1">
                                            {isEditing ? (
                                                <>
                                                    <button 
                                                        onClick={saveEdit}
                                                        className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                                                        title="Lagre"
                                                        disabled={loading}
                                                    >
                                                        <Save className="w-4 h-4" />
                                                    </button>
                                                    <button 
                                                        onClick={cancelEdit}
                                                        className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                                        title="Avbryt"
                                                        disabled={loading}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button 
                                                    onClick={() => startEdit(row)}
                                                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
                                                    title="Rediger"
                                                    disabled={loading}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Table Footer */}
            {sortedData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    <p>Ingen data funnet med gjeldende filtre</p>
                </div>
            ) : (
                <div className="p-4 border-t border-gray-200 text-sm text-gray-600">
                    Viser {sortedData.length} av {data.length} rader
                    {Object.values(selectedRows).filter(Boolean).length > 0 && (
                        <span className="ml-4">
                            • {Object.values(selectedRows).filter(Boolean).length} rader valgt
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};